import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { priceBasisLabel } from "@shared-core/domain/customerPriceBasis";
import { useListQuery } from "@shared-core/domain/useListQuery";
import { useCustomerDataAdapter } from "@shared-core/data/CustomerDataAdapterContext";
import { usePortalViewer } from "./PortalViewerContext";
import {
  CardOnlyNotice,
  isCardOnly,
  OrderingBlockedNotice,
  refusalReason,
} from "./OrderingBlockedNotice";
import { Card } from "../../app/components/primitives/card";
import { Badge } from "../../app/components/primitives/badge";
import { AlertTriangle, ChevronRight, Package } from "lucide-react";

// J1 step 1 — browse the customer's own catalogue
// (docs/features/customer-portal-ordering/01_product_brief.md §8): mirrors
// the CSR-facing catalogue browse experience, customer-scoped, masked
// pricing only (customer_price.py's shape — brief §11). Gated on ordering
// eligibility (§9d) before the list even renders — "not a UI-only
// restriction" (§6): a customer who can't order never sees a price-bearing
// catalogue at all, rather than seeing it and being refused at checkout.

export function PortalCataloguePage() {
  const adapter = useCustomerDataAdapter();
  const { viewKey, buildPortalUrl, sessionState } = usePortalViewer();
  const customerCode =
    sessionState.status === "success"
      ? (sessionState.data.ordering_customer_code ?? undefined)
      : undefined;

  const { state: eligibilityState } = useListQuery(() => {
    if (!customerCode) return Promise.resolve(null);
    return adapter.getOrderingEligibility(customerCode);
  }, [adapter, viewKey, customerCode]);
  const { state: itemsState, retry } = useListQuery(
    () => adapter.listCatalogueItems(),
    [adapter, viewKey],
  );

  if (
    sessionState.status === "loading" ||
    eligibilityState.status === "loading"
  ) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Loading…
      </Card>
    );
  }

  const eligibility =
    eligibilityState.status === "success" ? eligibilityState.data : null;
  const refusal = refusalReason(eligibility);
  if (refusal) {
    return (
      <div className="space-y-5">
        <h1 className="text-xl font-semibold tracking-tight">Catalogue</h1>
        <OrderingBlockedNotice reason={refusal} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Catalogue</h1>
        <p className="text-sm text-muted-foreground">
          Order any of these products yourself — pick, configure and confirm in
          a few steps.
        </p>
      </div>

      {isCardOnly(eligibility) && <CardOnlyNotice />}

      {itemsState.status === "loading" && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Loading the catalogue…
        </Card>
      )}

      {itemsState.status === "error" && (
        <Card className="flex flex-col items-center gap-3 p-8 text-center">
          <AlertTriangle className="size-6 text-muted-foreground" />
          <p className="font-medium">We couldn&apos;t load the catalogue</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            {itemsState.message}
          </p>
          <button
            type="button"
            onClick={retry}
            className="text-sm text-primary underline"
          >
            Try again
          </button>
        </Card>
      )}

      {itemsState.status === "success" && itemsState.data.length === 0 && (
        <Card className="flex flex-col items-center gap-2 p-10 text-center">
          <Package className="size-6 text-muted-foreground" />
          <p className="font-medium">Nothing in your catalogue yet</p>
          <p className="max-w-sm text-sm text-muted-foreground">
            Contact your rep to get products set up for self-serve ordering.
          </p>
        </Card>
      )}

      {itemsState.status === "success" && itemsState.data.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {itemsState.data.map((item) => (
            <Link
              key={item.item_id}
              to={buildPortalUrl(
                paths.portalCatalogueItem({ itemId: item.item_id }),
              )}
            >
              <Card className="flex h-full items-start justify-between gap-3 p-4 transition-colors hover:bg-accent/40">
                <div className="min-w-0 space-y-1">
                  <p className="font-medium">{item.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {item.product_family}
                  </p>
                  {item.quoted_to_order ? (
                    <Badge variant="neutral">Quoted to order</Badge>
                  ) : item.price ? (
                    <p className="text-sm">
                      From {formatQuotedPrice(item.price.price_pence)}{" "}
                      <span className="text-xs text-muted-foreground">
                        · {priceBasisLabel(item.price.basis)}
                      </span>
                    </p>
                  ) : (
                    <Badge variant="neutral">Price on request</Badge>
                  )}
                </div>
                <ChevronRight className="mt-1 size-4 shrink-0 text-muted-foreground" />
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
